<template>
  <div>
    <transition name="fade">
      <div v-if="isSettingUp" class="setup-overlay">
        <div class="setup-content">
          <span class="setup-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22">
              <path d="M10 3h4v7h7v4h-7v7h-4v-7H3v-4h7z" fill="#fff" />
            </svg>
          </span>
          <h2 class="setup-title">Signing you in</h2>
          <p class="setup-subtitle">Getting your clinic's data ready</p>
          <div class="setup-progress">
            <div class="setup-progress__head">
              <span>Preparing clinic data</span>
              <span class="setup-progress__percent">{{ setupProgress }}%</span>
            </div>
            <div class="setup-progress__track">
              <div
                class="setup-progress__fill"
                :style="{ width: setupProgress + '%' }"
              ></div>
            </div>
            <p v-if="setupPendingLabels" class="setup-progress__detail">
              Loading {{ setupPendingLabels }}…
            </p>
            <p v-else class="setup-progress__detail">
              Done — opening your workspace…
            </p>
          </div>
        </div>
      </div>
    </transition>

    <div class="auth">
      <div class="auth__shell">
        <!-- Brand field -->
        <aside class="auth__brand">
          <div class="brand">
            <span class="brand__mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path d="M10 3h4v7h7v4h-7v7h-4v-7H3v-4h7z" fill="#fff" />
              </svg>
            </span>
            <span class="brand__word">UCEMR</span>
          </div>

          <div class="brand__body">
            <div class="brand__lead">
              <h1 class="brand__headline">Clinical records, kept in order.</h1>
              <p class="brand__sub">
                Secure access for clinical and front-desk teams, across every
                branch.
              </p>
            </div>

            <ul class="brand__meta">
              <li>Encrypted patient records</li>
              <li>Role-based access control</li>
              <li>Two-step verification</li>
            </ul>
          </div>

          <div class="brand__foot">UCEMR · Electronic Medical Records</div>
        </aside>

        <!-- Sign-in panel -->
        <main class="auth__panel">
          <div class="auth__form">
            <div class="brand brand--compact">
              <span class="brand__mark" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path d="M10 3h4v7h7v4h-7v7h-4v-7H3v-4h7z" fill="#fff" />
                </svg>
              </span>
              <span class="brand__word">UCEMR</span>
            </div>

            <div v-if="step === 0" class="form-head">
              <h2 class="form-title">Sign in</h2>
              <p class="form-sub">
                Enter your credentials to access your workspace.
              </p>
            </div>
            <div v-if="step === 1" class="form-head">
              <h2 class="form-title">Two-step verification</h2>
              <p class="form-sub">
                Enter the 6-digit code from your authenticator app to continue.
              </p>
              <p class="download-link">
                No authenticator app?
                <span class="link-button" @click="openModel">Download one</span>
              </p>
            </div>

            <validation-observer v-if="step === 0" ref="loginInformation">
              <form @submit.prevent class="login-form">
                <validation-provider rules="required" v-slot="{ errors }">
                  <div class="field">
                    <label for="username">Username</label>
                    <div class="control">
                      <input
                        type="text"
                        v-model="userData.user_name"
                        name="username"
                        id="username"
                        placeholder="Enter your username"
                      />
                      <div class="control__icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          width="18"
                          height="18"
                        >
                          <circle cx="12" cy="7" r="4"></circle>
                          <path
                            d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                          ></path>
                        </svg>
                      </div>
                    </div>
                    <p class="error" v-if="userErrorMessage || errors[0]">
                      {{ userErrorMessage || "Username is required" }}
                    </p>
                  </div>
                </validation-provider>
                <validation-provider rules="required" v-slot="{ errors }">
                  <div class="field">
                    <label for="password">Password</label>
                    <div class="control">
                      <input
                        type="password"
                        v-model="userData.password"
                        name="password"
                        id="password"
                        placeholder="Enter your password"
                      />
                      <div class="control__icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          width="18"
                          height="18"
                        >
                          <rect
                            x="3"
                            y="11"
                            width="18"
                            height="11"
                            rx="2"
                            ry="2"
                          ></rect>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                        </svg>
                      </div>
                    </div>
                    <p class="error" v-if="errorMessage || errors[0]">
                      {{ errorMessage || "Password is required" }}
                    </p>
                  </div>
                </validation-provider>
                <div class="login-options">
                  <label class="remember-me">
                    <input
                      type="checkbox"
                      v-model="rememberMe"
                      class="remember-me-checkbox"
                    />
                    <span>Remember me</span>
                  </label>
                  <span
                    @click="forgetPassword"
                    class="link-button forget-password"
                    >Forgot password?</span
                  >
                </div>
                <Button
                  :loader="loading"
                  className="button--primary submit login-button"
                  :onClick="handleLogin"
                  title="Sign in"
                ></Button>
                <p class="form-note">Protected with two-step verification.</p>
              </form>
            </validation-observer>

            <div v-if="step === 1" class="otp-container">
              <div class="qr-code" v-if="!this.userInfo.is2FaActive">
                <img :src="qrCodeUrl" alt="Two-factor setup QR code" />
              </div>
              <div class="field">
                <label for="otp">Verification code</label>
                <div class="control">
                  <Input
                    type="text"
                    v-model="optCode"
                    class="otp-input"
                    @input="checkOtpLength"
                    name="otp"
                    id="otp"
                    placeholder="Enter 6-digit code"
                  />
                  <div class="control__icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      width="18"
                      height="18"
                    >
                      <path
                        d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"
                      ></path>
                    </svg>
                  </div>
                </div>
                <p class="error" v-if="otpError">
                  That code didn't match. Try again.
                </p>
              </div>
              <Button
                :loader="verifyLoading"
                className="button--primary submit login-button"
                :onClick="verfyOtp"
                title="Verify code"
              ></Button>
            </div>
          </div>

          <div class="auth__attribution" aria-label="Powered by UConnect">
            <span class="auth__attribution-text">Powered by</span>
            <img
              src="@/assets/uconnect.png"
              alt="UConnect"
              class="auth__attribution-logo"
            />
          </div>
        </main>
      </div>
    </div>

    <modal
      title="Download Authenticator Application "
      name="downloadModel"
      class="custom-modal"
      height="auto"
      width="50%"
      :adaptive="true"
    >
      <template v-slot:content>
        <div class="confirm-modal">
          <div>
            <p class="text-center">
              Android — download it from the Play Store or scan here.
            </p>
            <div class="flex justify-center" style="width: 100%;">
              <img
                src="../assets/authenticator.playstore.png"
                style="width: 12rem;height: 12rem;"
                alt=""
              />
            </div>
          </div>
          <div>
            <p class="text-center">
              iOS — download it from the App Store or scan here.
            </p>
            <div class="flex justify-center" style="width: 100%;">
              <img
                src="../assets/authenticator.appstore.png"
                style="width: 12rem;height: 12rem;"
                alt=""
              />
            </div>
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import Button from "@/components/Button";
import { mapActions, mapGetters } from "vuex";
import Input from "@/components/Input";
import { api } from "@/axios";
import Modal from "@/components/Modal";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import platform from "platform";
import { prefetchRegistrationCache, deleteCacheDB } from "@/utils/cacheDB";

export default {
  components: {
    Button,
    ValidationObserver,
    ValidationProvider,
    Input,
    Modal
  },
  data() {
    return {
      userData: {
        user_name: "",
        password: "",
        ip: "",
        location: "",
        os: "",
        browser: ""
      },
      optCode: "",
      step: 0,
      qrCodeUrl: "",
      verifyLoading: false,
      otpError: false,
      os: "",
      ip: "",
      location: "",
      loading: false,
      rememberMe: false,
      isSettingUp: false,
      setupSteps: [
        {
          label: "Services & prices",
          keys: ["services:all"],
          doneKeys: [],
          done: false
        },
        {
          label: "Referring doctors & hospitals",
          keys: ["lookup:doctor", "lookup:hospital"],
          doneKeys: [],
          done: false
        },
        {
          label: "Clinic doctors",
          keys: ["userDoctors"],
          doneKeys: [],
          done: false
        },
        {
          label: "Branches",
          keys: ["branches"],
          doneKeys: [],
          done: false
        }
      ]
    };
  },
  methods: {
    ...mapActions({
      getPermissions: "bussiness/getPermissions"
    }),
    convertBase64ToImageUrl(base64Url) {
      // Check if the input is a base64 URL
      const regex = /^data:(image\/\w+);base64,/; // Regular expression to match base64 URLs
      if (!regex.test(base64Url)) {
        throw new Error("Invalid base64 URL");
      }

      // Extract the content type from the base64 URL
      // prettier-ignore
      // eslint-disable-next-line no-useless-escape
      const contentType = base64Url.match(/^data:([A-Za-z-+\/]+);base64,/)[1];

      // Convert base64 to binary
      const binary = atob(base64Url.split(",")[1]);

      // Create a Uint8Array to store the binary data
      const array = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        array[i] = binary.charCodeAt(i);
      }

      // Create a Blob from the binary data
      const blob = new Blob([array], { type: contentType });

      // Create a URL for the Blob
      const imageUrl = URL.createObjectURL(blob);

      return imageUrl;
    },
    openModel() {
      this.$modal.show("downloadModel");
    },
    checkOtpLength() {
      if (this.optCode.length === 6) {
        this.verfyOtp();
      }
    },
    markSetupProgress(key) {
      this.setupSteps.forEach(step => {
        if (step.keys.includes(key) && !step.doneKeys.includes(key)) {
          step.doneKeys.push(key);
          step.done = step.doneKeys.length === step.keys.length;
        }
      });
    },
    loginLogic() {
      this.$store.dispatch("auth/userLoginProgress", true);
      const userRole = this.role && this.role.map(item => item.name);
      const roles = ["receptionist", "technician"];
      this.getPermissions().then(() => {
        let redirectPath = "/";
        if (userRole && userRole.some(item => roles.includes(item))) {
          redirectPath = "/registration/register";
        } else if (
          userRole &&
          userRole.length == 1 &&
          userRole.includes("doctor")
        ) {
          redirectPath = "/patients";
        } else if (userRole && userRole.includes("finance")) {
          redirectPath = "/doctors";
        } else if (userRole && userRole.includes("admin")) {
          redirectPath = "/users";
        }

        // Trigger 'Setting Up' overlay
        this.isSettingUp = true;

        // Keep the overlay up until the registration dropdown data is
        // stored in IndexedDB (it survives the hard refresh below), retrying
        // failed endpoints. The 30s cap stops a dead API from trapping the
        // user — the registration page re-fetches anything missing.
        const prefetchUntilStored = async () => {
          for (let attempt = 0; attempt < 3; attempt++) {
            if (await prefetchRegistrationCache(this.markSetupProgress)) {
              return;
            }
          }
        };
        const timeout = new Promise(resolve => setTimeout(resolve, 30000));
        Promise.race([prefetchUntilStored(), timeout]).finally(() => {
          window.location.href = redirectPath;
        });
      });
    },
    async verfyOtp() {
      try {
        this.verifyLoading = true;
        const payload = {
          userId: this.userInfo.id,
          otp: this.optCode
        };
        const res = await api.post("/2fa/verify", payload);
        if (res.data.isValid.isValid) {
          // Store Remember Me preference
          if (this.rememberMe) {
            localStorage.setItem("rememberMe", "true");
          } else {
            localStorage.removeItem("rememberMe");
          }
          this.loginLogic();
        } else {
          this.otpError = true;
          // await this.generateOTP();
          this.$store.dispatch("auth/userLoginProgress", false);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.verifyLoading = false;
      }
    },
    async fetchUserData() {
      this.userData.os = platform.os.family;
      this.userData.ip = await this.getLocalIP();
      this.userData.browser = `${platform.name} ${platform.version}`;
      this.userData.location = await this.getLocation();
    },
    async getLocalIP() {
      return new Promise((resolve, reject) => {
        const peerConnection = new RTCPeerConnection({
          iceServers: []
        });
        peerConnection.createDataChannel("");
        peerConnection
          .createOffer()
          .then(offer => peerConnection.setLocalDescription(offer))
          .catch(reject);

        peerConnection.onicecandidate = event => {
          if (event && event.candidate && event.candidate.candidate) {
            const candidate = event.candidate.candidate;
            const ipMatch = candidate.match(/(\d{1,3}\.){3}\d{1,3}/);
            if (ipMatch) {
              resolve(ipMatch[0]);
              peerConnection.onicecandidate = null; // No need to keep listening once we have the IP
            }
          }
        };

        setTimeout(() => {
          resolve(null); // Resolve to null if no IP found within a reasonable time
        }, 1000);
      });
    },
    async getLocation() {
      return new Promise(resolve => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            position => {
              const { latitude, longitude } = position.coords;
              resolve(`Lat: ${latitude}, Long: ${longitude}`);
            },
            error => {
              console.error("Error getting location:", error);
              resolve(null);
            }
          );
        } else {
          resolve(null);
        }
      });
    },
    checkRestrictedArea(location) {
      const country = location.split(", ").pop();
      return country === "Pakistan";
    },
    async handleLogin() {
      this.loading = true;
      this.fetchUserData();
      this.$refs.loginInformation.validate().then(async success => {
        if (!success) {
          this.loading = false;
          return;
        }
        // to form submit after this
        localStorage.clear();
        sessionStorage.clear();
        // Clear IndexedDB
        const dbName = "ReportsDB";
        const request = indexedDB.deleteDatabase(dbName);

        request.onerror = event => {
          console.error("Error deleting database:", event.target.error);
        };

        request.onsuccess = event => {
          console.log("Database deleted successfully", event);
        };
        deleteCacheDB();
        await this.$store
          .dispatch("auth/login", { vm: this, payload: this.userData })
          .then(async () => {
            // Store Remember Me preference
            if (this.rememberMe) {
              localStorage.setItem("rememberMe", "true");
            } else {
              localStorage.removeItem("rememberMe");
            }

            if (
              !this.userInfo.is2FaActive &&
              !this.errorMessage &&
              !this.userErrorMessage &&
              !this.userTooManyRequest
            ) {
              await this.generateOTP();
            }
            if (
              this.userInfo &&
              !this.errorMessage &&
              !this.userErrorMessage &&
              this.userInfo.tenant2Fa
            ) {
              this.step = 1;
            } else if (
              this.userInfo &&
              !this.errorMessage &&
              !this.userErrorMessage &&
              this.userInfo.is2FaActive
            ) {
              this.step = 1;
            } else if (
              this.userInfo &&
              !this.errorMessage &&
              !this.userErrorMessage
            ) {
              // this.step = 1;
              // this.verfyOtp()
              this.loginLogic();
            }
          })
          .catch(() => {
            this.loading = false;
            if (this.$route.path !== "/login") {
              this.$router.push("/login");
            }
            this.$store.dispatch("auth/userLoginProgress", false);
          })
          .finally(() => {
            this.loading = false;
          });
      });
    },
    async generateOTP() {
      const payload = {
        user: {
          id: this.userInfo.id
        }
      };
      const res = await api.post("/2fa/generate", payload);
      this.qrCodeUrl = res.data.qrCodeDataURL;
    },
    forgetPassword() {
      if (this.userData.user_name) {
        const payload = {
          user_name: this.userData.user_name
        };
        this.$store.dispatch("auth/forgetPassword", payload);
        this.$toasted
          .success("We've sent you an email with a link to reset your password")
          .goAway("3000");
      } else {
        this.$toasted
          .show("Username is required for reseting password")
          .goAway("3000");
      }
    }
  },
  computed: {
    setupProgress() {
      const total = this.setupSteps.reduce((n, s) => n + s.keys.length, 0);
      const done = this.setupSteps.reduce((n, s) => n + s.doneKeys.length, 0);
      return Math.round((done / total) * 100);
    },
    setupPendingLabels() {
      return this.setupSteps
        .filter(step => !step.done)
        .map(step => step.label.toLowerCase())
        .join(", ");
    },
    ...mapGetters({
      role: "auth/role",
      userInfo: "auth/userInfo",
      isLoggedIn: "auth/isLoggedIn",
      fetchBranch: "auth/fetchBranch",
      errorMessage: "auth/errorMessage",
      userErrorMessage: "auth/userErrorMessage",
      userTooManyRequest: "auth/userTooManyRequest"
      // loading: "loading",
    })
  },
  async mounted() {
    await this.fetchUserData();
    // Check if Remember Me was previously set
    if (localStorage.getItem("rememberMe") === "true") {
      this.rememberMe = true;
    }
  }
};
</script>

<style scoped>
.auth {
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: var(--canvas);
  padding: 28px 20px;
}

.auth *,
.auth *::before,
.auth *::after {
  box-sizing: border-box;
}

.auth__shell {
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  width: 100%;
  max-width: 1040px;
  min-height: 600px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-md);
}

/* ---------- Brand field ---------- */
.auth__brand {
  position: relative;
  background: var(--grad-brand);
  color: #fff;
  padding: 48px 44px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.brand__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: var(--radius-md);
  background: var(--grad-accent);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18);
  flex-shrink: 0;
}

.brand__word {
  font-size: 1.0625rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  color: #fff;
}

.brand__body {
  display: flex;
  flex-direction: column;
  padding: 32px 0;
}

.brand__headline {
  font-size: var(--fs-display);
  font-weight: 600;
  line-height: 1.25;
  letter-spacing: var(--tracking-tight);
  color: #fff;
  max-width: 15ch;
  margin: 0;
}

.brand__sub {
  margin: 14px 0 0;
  font-size: var(--fs-body);
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.66);
  max-width: 34ch;
}

.brand__meta {
  list-style: none;
  margin: 30px 0 0;
  padding: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.brand__meta li {
  display: flex;
  align-items: center;
  padding: 12px 0;
  font-size: var(--fs-sm);
  color: rgba(255, 255, 255, 0.78);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.brand__meta li::before {
  content: "";
  width: 5px;
  height: 5px;
  border-radius: 1px;
  background: var(--accent-500);
  margin-right: 12px;
  flex-shrink: 0;
}

.brand__foot {
  margin-top: 28px;
  font-size: var(--fs-eyebrow);
  letter-spacing: var(--tracking-eyebrow);
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
}

/* ---------- Sign-in panel ---------- */
.auth__panel {
  position: relative;
  background: var(--surface);
  padding: 48px 48px 72px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.auth__attribution {
  position: absolute;
  bottom: 28px;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
}

.auth__attribution-text {
  font-size: var(--fs-xs);
  color: var(--muted-soft);
  letter-spacing: 0.02em;
}

.auth__attribution-logo {
  height: 24px;
  width: auto;
  max-width: 96px;
  object-fit: contain;
  opacity: 0.88;
}

.auth__form {
  width: 100%;
  max-width: 348px;
}

.brand--compact {
  display: none;
  margin-bottom: 28px;
}

.brand--compact .brand__mark {
  width: 28px;
  height: 28px;
}

.brand--compact .brand__word {
  color: var(--ink-900);
  font-size: 1rem;
}

.form-head {
  margin-bottom: 26px;
}

.form-title {
  font-size: var(--fs-h1);
  font-weight: 600;
  letter-spacing: var(--tracking-tight);
  color: var(--ink-900);
  margin: 0;
}

.form-sub {
  margin: 8px 0 0;
  color: var(--muted);
  font-size: var(--fs-body);
  line-height: 1.5;
}

.login-form {
  display: flex;
  flex-direction: column;
}

.login-form .field {
  margin-bottom: 20px;
}

.field {
  display: flex;
  flex-direction: column;
}

.field label {
  font-size: var(--fs-sm);
  font-weight: 500;
  color: var(--text);
  margin-bottom: 7px;
}

.control {
  position: relative;
  width: 100%;
}

.control__icon {
  position: absolute;
  right: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted-soft);
  pointer-events: none;
}

.control input {
  width: 100%;
  height: 44px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  padding: 0 42px 0 13px;
  font-size: var(--fs-body);
  color: var(--text);
  transition: border-color var(--dur) var(--ease),
    box-shadow var(--dur) var(--ease);
}

.control input::placeholder {
  color: var(--muted-soft);
}

.control input:focus {
  outline: none;
  border-color: var(--accent-600);
  box-shadow: var(--ring);
}

.error {
  color: var(--danger);
  font-size: var(--fs-xs);
  margin: 7px 0 0;
  font-weight: 500;
}

.login-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 15px;
}

.link-button {
  font-size: var(--fs-sm);
  color: var(--accent-600);
  font-weight: 500;
  cursor: pointer;
  transition: color var(--dur) var(--ease);
}

.link-button:hover {
  color: var(--accent-700);
  text-decoration: underline;
}

.remember-me {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.remember-me span {
  font-size: var(--fs-sm);
  color: var(--muted);
}

.remember-me-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: var(--accent-600);
  margin: 0;
}

.login-button {
  background: var(--grad-accent);
  border: none;
  border-radius: var(--radius-sm);
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  height: 44px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: var(--shadow-sm), inset 0 1px 0 rgba(255, 255, 255, 0.14);
  transition: filter var(--dur) var(--ease), box-shadow var(--dur) var(--ease);
}

.login-button:hover {
  background: var(--grad-accent-hover);
}

.form-note {
  margin: 16px 0 0;
  font-size: var(--fs-xs);
  color: var(--muted-soft);
  text-align: center;
}

.download-link {
  margin: 12px 0 0;
  font-size: var(--fs-sm);
  color: var(--muted);
}

/* ---------- 2FA ---------- */
.otp-container {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.otp-input {
  width: 100%;
}

.qr-code {
  display: flex;
  justify-content: center;
  width: 100%;
}

.qr-code img {
  max-width: 180px;
  border-radius: var(--radius-md);
  border: 1px solid var(--line);
  padding: 10px;
  background: var(--surface);
}

/* ---------- Responsive ---------- */
@media (max-width: 880px) {
  .auth {
    padding: 0;
  }

  .auth__shell {
    grid-template-columns: 1fr;
    max-width: 460px;
    min-height: 100vh;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }

  .auth__brand {
    display: none;
  }

  .brand--compact {
    display: flex;
  }

  .auth__panel {
    padding: 64px 28px 68px;
    align-items: flex-start;
  }

  .auth__attribution {
    bottom: 24px;
  }
}

@media (max-width: 480px) {
  .auth__panel {
    padding: 48px 22px 64px;
  }

  .auth__form {
    max-width: 100%;
  }
}

/* ---------- Sign-in transition overlay ---------- */
/* The brand field takes over the screen while the clinic's working data
   is stored locally; each step checks off as its dataset actually lands. */
.setup-overlay {
  position: fixed;
  inset: 0;
  background: var(--grad-brand);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.setup-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: fadeInUp 0.5s var(--ease);
}

.setup-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: var(--radius-md);
  background: var(--grad-accent);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18);
  animation: setup-pulse 2s var(--ease) infinite;
}

.setup-title {
  font-size: var(--fs-h1);
  font-weight: 600;
  letter-spacing: var(--tracking-tight);
  color: #fff;
  margin: 22px 0 6px;
}

.setup-subtitle {
  color: rgba(255, 255, 255, 0.6);
  font-size: var(--fs-body);
  margin: 0 0 28px;
}

.setup-progress {
  min-width: 320px;
  padding: 20px 24px;
  border-radius: var(--radius-lg);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  text-align: left;
}

.setup-progress__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: var(--fs-sm);
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 10px;
}

.setup-progress__percent {
  font-size: var(--fs-h2);
  font-weight: 600;
  color: #fff;
}

.setup-progress__track {
  height: 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;
}

.setup-progress__fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--accent-500), var(--accent-600));
  transition: width 0.45s var(--ease);
}

.setup-progress__detail {
  margin: 10px 0 0;
  font-size: var(--fs-xs);
  color: rgba(255, 255, 255, 0.55);
}

@keyframes setup-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(139, 118, 216, 0.35);
  }
  70% {
    box-shadow: 0 0 0 14px rgba(139, 118, 216, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(139, 118, 216, 0);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.35s var(--ease);
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
